import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useTheme,
  Stack,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Architecture as ArchitectureIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  CloudDone as CloudIcon,
  ArrowForward as ArrowForwardIcon,
  LocationCity as LocationCityIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const features = [
  {
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    title: 'Comprehensive Analysis',
    description: 'Get detailed feasibility reports with zoning, financial, and market analysis for any property.',
  },
  {
    icon: <ArchitectureIcon sx={{ fontSize: 40 }} />,
    title: 'Development Scenarios',
    description: 'Explore renovation, extension, teardown, and ADU options with accurate cost projections.',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: 'Real-Time Data',
    description: 'Access live property data from authenticated ArcGIS APIs and municipal databases.',
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
    title: 'Market Intelligence',
    description: 'Compare properties with market trends, rental rates, and comparable sales data.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: 'Secure & Reliable',
    description: 'Enterprise-grade security with encrypted data transmission and secure authentication.',
  },
  {
    icon: <CloudIcon sx={{ fontSize: 40 }} />,
    title: 'Cloud-Based',
    description: 'Access your analyses from anywhere with automatic cloud backup and sync.',
  },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation Bar */}
      <AppBar 
        position="fixed" 
        color="inherit" 
        elevation={0}
        sx={{ 
          bgcolor: 'rgba(255,255,255,0.9)', 
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <LocationCityIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1a73e8 0%, #34a853 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              QuickBuildr
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            {isAuthenticated ? (
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
                sx={{ borderRadius: 2 }}
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="text"
                  component={Link}
                  to="/login"
                  sx={{ color: 'text.secondary' }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  component={Link}
                  to="/register"
                  sx={{ borderRadius: 2 }}
                >
                  Get Started
                </Button>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ pt: 12 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Chip 
                label="Powered by Real-Time ArcGIS Data" 
                color="primary" 
                variant="outlined"
                sx={{ mb: 3 }}
              />
              
              <Typography 
                variant="h1" 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  mb: 3,
                  background: 'linear-gradient(135deg, #1a73e8 0%, #34a853 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Property Development
                <br />
                Made Simple
              </Typography>
              
              <Typography 
                variant="h5" 
                color="text.secondary" 
                sx={{ mb: 5, fontWeight: 400, maxWidth: 600, mx: 'auto' }}
              >
                Analyze property feasibility in Massachusetts with AI-powered insights, 
                real-time data, and comprehensive development scenarios.
              </Typography>

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ mb: 8 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/features"
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                  }}
                >
                  View Features
                </Button>
              </Stack>

              {/* Stats */}
              <Grid container spacing={4} justifyContent="center">
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box>
                    <Typography variant="h3" color="primary" fontWeight={700}>
                      10K+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Properties Analyzed
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box>
                    <Typography variant="h3" color="primary" fontWeight={700}>
                      98%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Accuracy Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box>
                    <Typography variant="h3" color="primary" fontWeight={700}>
                      {'<1min'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Analysis Time
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box>
                    <Typography variant="h3" color="primary" fontWeight={700}>
                      24/7
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Data Updates
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ mb: 2, fontWeight: 700 }}
            >
              Everything You Need
            </Typography>
            <Typography 
              variant="h6" 
              textAlign="center" 
              color="text.secondary" 
              sx={{ mb: 6, fontWeight: 400 }}
            >
              Comprehensive tools for property development analysis
            </Typography>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.5 }}
                    whileHover={{ y: -5 }}
                  >
                    <Card 
                      sx={{ 
                        height: '100%',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 0,
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: theme.shadows[4],
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box 
                          sx={{ 
                            color: 'primary.main',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 60,
                            height: 60,
                            borderRadius: 2,
                            bgcolor: (theme) => `${theme.palette.primary.main}15`,
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Ready to Start Building?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join thousands of developers and investors making smarter property decisions.
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  py: 1.5,
                  px: 4,
                  '&:hover': {
                    bgcolor: 'grey.100',
                  }
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  py: 1.5,
                  px: 4,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                Contact Sales
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <LocationCityIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  QuickBuildr.ai
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Professional property development analysis for Massachusetts real estate.
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Product
              </Typography>
              <Stack spacing={1}>
                <Link to="/features" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="text.secondary">Features</Typography>
                </Link>
                <Link to="/pricing" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="text.secondary">Pricing</Typography>
                </Link>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Company
              </Typography>
              <Stack spacing={1}>
                <Link to="/about" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="text.secondary">About</Typography>
                </Link>
                <Link to="/contact" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="text.secondary">Contact</Typography>
                </Link>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Legal
              </Typography>
              <Stack spacing={1}>
                <Link to="/privacy" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="text.secondary">Privacy</Typography>
                </Link>
                <Link to="/terms" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="text.secondary">Terms</Typography>
                </Link>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Support
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  support@quickbuildr.ai
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  781-528-3339
                </Typography>
              </Stack>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              © 2025 QuickBuildr.ai. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;